/* global Word, fetch, btoa */
/**
 * Templates por TIPO de documento + regras (o "manual" que o modelo segue).
 *
 * Fluxo: no seletor de tipo o usuário escolhe o tipo (Proposta Técnica, Outro). Isso
 * (1) insere o `template.docx` daquele tipo no documento — capa/cabeçalho/rodapé
 * já prontos — e (2) carrega as regras (base Norven + regras do tipo) que são
 * enviadas ao backend e injetadas no prompt. A seleção é obrigatória e não passa
 * pelo modelo. A conversa fica "ligada" ao tipo escolhido.
 *
 * Cada tipo vive em `public/templates/<id>/` com `template.docx` + `rules.json`.
 * `_base/rules.json` (sem template) traz as regras base aplicadas a TODOS. O
 * catálogo de tipos selecionáveis está em `templates/index.json`.
 *
 * Por que inserir o .docx inteiro (insertFileFromBase64) em vez de recriar
 * capa/cabeçalho/rodapé via Office.js: o .docx já tem banners, margens e campos
 * da capa na posição exata do design; o overload no nível do Document copia
 * headers/footers/section properties junto — resultado idêntico ao arquivo.
 *
 * Campos da capa: nos templates que usam caixas de texto flutuantes,
 * `tagCoverShapes` envolve cada campo numa content control com tag, para o
 * modelo preenchê-los via `fill_content_control`. Requer `body.shapes`
 * (WordApiDesktop 1.2); best-effort (degrada em hosts sem suporte). Templates
 * com capa diferente simplesmente não casam os placeholders — sem erro.
 */
// `withTrackChangesSuspended` vive em tools.ts (dono do estado de revisão).
// Sem ciclo: tools.ts não importa template.ts.
import { isCoverRuleParagraph, withTrackChangesSuspended } from './tools';
const TEMPLATES_BASE = 'templates';
// Ordem em que as seções do rules.json entram no prompt. Chaves ausentes são
// puladas; chaves fora desta lista entram depois, na ordem do arquivo.
const RULE_SECTION_ORDER = [
    'source_basis',
    'document_variant_contract',
    'strict_formatting_contract',
    'cover_contract',
    'style_ref_contract',
    'numbering_contract',
    'page_layout_contract',
    'protected_regions',
    'color_policy',
    'table_contract',
    'list_contract',
    'human_tone_preferences',
    'validation_checklist_before_final_answer',
    'template_observations',
    // A porta do modelo de contratação vem ANTES da estrutura: ela decide a forma
    // do escopo, e escrever escopo sem ela é o defeito nº 1 da revisão.
    'pre_writing_gate',
    'contracting_models',
    'proposal_levels',
    'document_structure',
    'stage_recipe',
    'outsourcing_scope_form',
    'out_of_scope_contract',
    'always_on_premises',
    'schedule',
    'team',
    'governance',
    'acceptance',
    'table_patterns',
    'type_specific_formatting',
    'software_specific',
    'department_overlays',
    'content_rules',
    'cover_policy',
    'file_naming',
    'reproves_in_review',
    'checklist_before_done',
];
const RULE_SECTION_TITLES = {
    source_basis: 'Fonte de verdade e precedencia',
    document_variant_contract: 'Variantes por tipo de documento',
    strict_formatting_contract: 'Contrato estrito de formatacao',
    cover_contract: 'Capa',
    style_ref_contract: 'StyleRefs e estilos permitidos',
    numbering_contract: 'Numeracao de topicos',
    page_layout_contract: 'Layout de pagina',
    protected_regions: 'Regioes protegidas',
    color_policy: 'Paleta e politica de cores',
    table_contract: 'Tabelas',
    list_contract: 'Listas',
    human_tone_preferences: 'Texto que nao parece escrito por IA (preferencia)',
    validation_checklist_before_final_answer: 'Checklist final obrigatorio',
    template_observations: 'Observacoes extraidas do template',
    document_structure: 'Estrutura esperada do documento',
    type_specific_formatting: 'Formatacao especifica do tipo',
    content_rules: 'Regras de conteudo',
    cover_policy: 'Capa',
    pre_writing_gate: 'Porta obrigatoria antes de escrever o escopo',
    contracting_models: 'Modelos de contratacao (escolher UM)',
    proposal_levels: 'Niveis da proposta',
    stage_recipe: 'Receita de etapa',
    outsourcing_scope_form: 'Forma do escopo em outsourcing',
    out_of_scope_contract: 'Fora do escopo e trava de fronteira',
    always_on_premises: 'Premissas que sempre entram',
    schedule: 'Cronograma',
    team: 'Equipe',
    governance: 'Governanca e mudancas',
    acceptance: 'Aceite',
    table_patterns: 'Colunas padrao das tabelas',
    spacing_rules: 'Respiro e parágrafos vazios',
    lists_and_figures: 'Listas e figuras',
    software_specific: 'Especifico do perfil software',
    department_overlays: 'Overlays por unidade de negocio',
    file_naming: 'Nome do arquivo',
    reproves_in_review: 'O que reprova na revisao',
    checklist_before_done: 'Checklist antes de concluir',
};
const RULE_METADATA_KEYS = new Set(['id', 'name', 'config_version', 'instructions']);
/**
 * O documento já tem ALGO (não é uma folha em branco)? Usado para decidir se
 * pergunta ao usuário (apagar × formatar) antes de aplicar o template.
 *
 * Não basta olhar `body.text`: uma CAPA (própria dos templates Norven) vive em
 * caixas de texto flutuantes (shapes), content controls e cabeçalho/rodapé — o
 * corpo pode estar vazio de texto e ainda assim a página estar cheia. Olhar só
 * o texto fazia um documento "só capa" ser tratado como vazio, então o template
 * era inserido POR CIMA (em sequência) e a capa antiga ia para a página 2 (bug
 * relatado). Aqui checamos vários sinais; qualquer um presente = tem conteúdo.
 *
 * ⚠️ NA DÚVIDA, RESPONDE `true`. O caminho "documento vazio" aplica o template
 * como `replace` (troca o corpo inteiro — é o que evita a quebra de seção e as
 * páginas em branco do `start`), então um falso "vazio" APAGARIA o documento.
 * Por isso tanto uma falha na checagem quanto a ausência da API de shapes
 * (host sem WordApiDesktop 1.2, onde não há como descartar uma capa flutuante)
 * viram "tem conteúdo": o usuário decide no modal. Um clique a mais é barato;
 * apagar o documento de alguém, não.
 */
export async function documentHasContent() {
    try {
        return await Word.run(async (context) => {
            const body = context.document.body;
            body.load('text');
            // getFirstOrNullObject: checa EXISTÊNCIA sem carregar a coleção inteira.
            const firstTable = body.tables.getFirstOrNullObject();
            firstTable.load('isNullObject');
            const firstPicture = body.inlinePictures.getFirstOrNullObject();
            firstPicture.load('isNullObject');
            const firstControl = context.document.contentControls.getFirstOrNullObject();
            firstControl.load('isNullObject');
            await context.sync();
            if ((body.text || '').replace(/\s/g, '').length > 0)
                return true;
            if (!firstTable.isNullObject)
                return true;
            if (!firstPicture.isNullObject)
                return true;
            if (!firstControl.isNullObject)
                return true; // capa marcada por tagCoverShapes
            // Capa flutuante (shapes) — WordApiDesktop 1.2; só chega aqui se os
            // sinais mais baratos não acharam nada.
            try {
                const firstShape = body.shapes.getFirstOrNullObject();
                firstShape.load('isNullObject');
                await context.sync();
                if (!firstShape.isNullObject)
                    return true;
            }
            catch (_a) {
                // Host sem a API: não há como descartar uma capa flutuante. Assume que
                // TEM conteúdo (ver aviso no doc-comment) em vez de deixar o `replace`
                // apagar às cegas.
                console.warn('[documentHasContent] sem API de shapes; assumindo que há conteúdo.');
                return true;
            }
            return false;
        });
    }
    catch (e) {
        // Não conseguimos afirmar que está vazio ⇒ trata como "tem conteúdo" e
        // deixa a decisão para o usuário no modal.
        console.warn('[documentHasContent] falhou; assumindo que há conteúdo:', e);
        return true;
    }
}
/** Normaliza texto p/ comparar com placeholders de capa (minúsculo, sem "_"). */
function normalizeCoverText(s) {
    return s.toLowerCase().replace(/[_\s]+/g, ' ').trim();
}
/** Conjunto (lazy) dos textos-placeholder da capa, para filtrar campos vazios. */
let coverPlaceholderCache = null;
function isCoverPlaceholderText(text) {
    if (!coverPlaceholderCache) {
        const raw = [COVER_DATE_PLACEHOLDER, '_'];
        for (const field of COVER_FIELDS)
            raw.push(...field.matches);
        coverPlaceholderCache = new Set(raw.map(normalizeCoverText));
    }
    return coverPlaceholderCache.has(normalizeCoverText(text));
}
const COVER_FIELDS = [
    {
        tag: 'document_type',
        matches: ['Tipo de Documento', 'Tipo do Documento', 'Proposta Técnica'],
    },
    { tag: 'document_title', matches: ['Título do documento', 'Título do relatório'] },
    {
        tag: 'client_name',
        matches: ['Nome da Área ou Empresa', 'Nome da área ou empresa', 'Nome da empresa', 'Nome do cliente'],
    },
];
const COVER_DATE_PLACEHOLDER = '17/02/2025';
function formatTodayPtBr() {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
}
async function fetchBase64(url) {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++)
        binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
/**
 * Envolve o CAMPO de cada caixa de texto da capa numa content control com a tag
 * correspondente, para que `fill_content_control` funcione sobre ela.
 * Best-effort: requer WordApiDesktop 1.2 (`body.shapes`); se indisponível, não
 * faz nada.
 *
 * A control delimita o parágrafo do VALOR, não a caixa inteira. Marcar
 * `shape.body` envolvia também o parágrafo do traço ("_") que fica acima do
 * campo — e `fill_content_control` escreve com `insertText(..., 'replace')`,
 * que troca TODO o conteúdo da control. Resultado: preencher o título ou o
 * cliente apagava o traço junto com o placeholder (o tipo de documento
 * mantinha o dele só porque ninguém o preenchia). Ver `fillContentControl`,
 * que também se protege disso para documentos criados antes desta correção.
 */
async function tagCoverShapes(context) {
    var _a;
    const shapes = context.document.body.shapes;
    shapes.load('items');
    await context.sync();
    for (const shape of shapes.items) {
        shape.body.load('text');
        shape.body.paragraphs.load('items/text');
    }
    await context.sync();
    for (const shape of shapes.items) {
        const text = shape.body.text || '';
        const field = COVER_FIELDS.find((f) => f.matches.some((match) => text.toLocaleLowerCase('pt-BR').includes(match.toLocaleLowerCase('pt-BR'))));
        if (!field)
            continue;
        // O parágrafo do campo é o que contém o placeholder; o traço nunca casa.
        // Sem match (template alterado), fica o último não-traço — em capa Norven
        // o valor é sempre a última linha da caixa.
        const paras = shape.body.paragraphs.items;
        const candidatos = paras.filter((p) => !isCoverRuleParagraph(p.text));
        const alvo = (_a = candidatos.find((p) => field.matches.some((match) => (p.text || '')
            .toLocaleLowerCase('pt-BR')
            .includes(match.toLocaleLowerCase('pt-BR'))))) !== null && _a !== void 0 ? _a : candidatos[candidatos.length - 1];
        if (!alvo)
            continue;
        const cc = alvo.insertContentControl();
        cc.tag = field.tag;
        cc.title = field.tag;
    }
    await context.sync();
}
/**
 * Substitui o placeholder de data do rodapé da capa pela data do documento —
 * `date` quando ele já tinha uma (formatar um documento existente não troca a
 * data dele pela de hoje), senão a atual (dd/MM/yyyy). Best-effort: se o
 * placeholder não existir (.docx alterado), não faz nada.
 */
async function stampCoverDate(context, date) {
    const footer = context.document.sections.getFirst().getFooter(Word.HeaderFooterType.firstPage);
    const results = footer.search(COVER_DATE_PLACEHOLDER, { matchCase: false });
    results.load('items');
    await context.sync();
    if (results.items.length === 0)
        return;
    const valor = results.items[0].insertText(date !== null && date !== void 0 ? date : formatTodayPtBr(), Word.InsertLocation.replace);
    await context.sync();
    // A data vira CAMPO da capa, como título e cliente: `fill_content_control`
    // (tag document_date) a troca quando ela vem do documento — a folha de rosto
    // antiga de um documento formatado, por exemplo — sem mexer no rodapé.
    try {
        const cc = valor.insertContentControl();
        cc.tag = 'document_date';
        cc.title = 'document_date';
        await context.sync();
    }
    catch (e) {
        console.warn('[stampCoverDate] data sem campo marcado (fica como texto):', e);
    }
}
/** Lê o catálogo de tipos selecionáveis (templates/index.json). */
export async function loadTemplateCatalog() {
    var _a;
    const res = await fetch(`${TEMPLATES_BASE}/index.json`);
    if (!res.ok)
        throw new Error(`Não consegui carregar os tipos de documento (${res.status}).`);
    const data = (await res.json());
    return (_a = data.templates) !== null && _a !== void 0 ? _a : [];
}
/** Lê um rules.json inteiro. Campos estruturados também entram no prompt. */
export async function readRulesJson(id) {
    try {
        const res = await fetch(`${TEMPLATES_BASE}/${id}/rules.json`);
        if (!res.ok)
            return null;
        return (await res.json());
    }
    catch (_a) {
        return null;
    }
}
function isEmptyRuleValue(value) {
    if (value == null)
        return true;
    if (typeof value === 'string')
        return value.trim().length === 0;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object')
        return Object.keys(value).length === 0;
    return false;
}
function isRuleScalar(value) {
    return value == null || ['string', 'number', 'boolean'].includes(typeof value);
}
function formatRuleScalar(value) {
    if (value == null)
        return 'null';
    if (typeof value === 'string')
        return value;
    return String(value);
}
function renderRuleValue(value, depth = 0) {
    const indent = '  '.repeat(depth);
    if (isRuleScalar(value))
        return `${indent}${formatRuleScalar(value)}`;
    if (Array.isArray(value)) {
        return value
            .map((item) => {
            if (isRuleScalar(item))
                return `${indent}- ${formatRuleScalar(item)}`;
            return `${indent}- item:\n${renderRuleValue(item, depth + 1)}`;
        })
            .join('\n');
    }
    if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([key, item]) => !key.startsWith('_') && !isEmptyRuleValue(item));
        return entries
            .map(([key, item]) => {
            const label = `\`${key}\``;
            if (isRuleScalar(item))
                return `${indent}- ${label}: ${formatRuleScalar(item)}`;
            if (Array.isArray(item) && item.every(isRuleScalar)) {
                return `${indent}- ${label}: ${item.map(formatRuleScalar).join('; ')}`;
            }
            return `${indent}- ${label}:\n${renderRuleValue(item, depth + 1)}`;
        })
            .join('\n');
    }
    return `${indent}${String(value)}`;
}
function renderRuleSection(key, value) {
    var _a;
    const title = (_a = RULE_SECTION_TITLES[key]) !== null && _a !== void 0 ? _a : key.replace(/_/g, ' ');
    return `### ${title}\n${renderRuleValue(value)}`;
}
function renderRulesJson(data, scope) {
    var _a, _b, _c;
    if (!data)
        return '';
    const instructions = typeof data.instructions === 'string' ? data.instructions.trim() : '';
    const sectionKeys = [
        ...RULE_SECTION_ORDER.filter((key) => !isEmptyRuleValue(data[key])),
        ...Object.keys(data).filter((key) => !RULE_METADATA_KEYS.has(key) &&
            !key.startsWith('_') &&
            !RULE_SECTION_ORDER.includes(key) &&
            !isEmptyRuleValue(data[key])),
    ];
    const title = scope === 'base'
        ? `# Regras base Norven: ${(_a = data.name) !== null && _a !== void 0 ? _a : '_base'}`
        : `# Regras especificas do tipo: ${(_c = (_b = data.name) !== null && _b !== void 0 ? _b : data.id) !== null && _c !== void 0 ? _c : 'documento'}`;
    const parts = [title];
    if (data.config_version)
        parts.push(`Versao das regras: ${data.config_version}`);
    if (instructions)
        parts.push(`## Instrucoes narrativas\n${instructions}`);
    if (sectionKeys.length) {
        parts.push('## Especificacoes estruturadas do rules.json\n' +
            'Os campos abaixo sao contrato operacional: nomes, valores, listas e objetos devem ser obedecidos literalmente.');
        parts.push(...sectionKeys.map((key) => renderRuleSection(key, data[key])));
    }
    return parts.join('\n\n');
}
/**
 * Combina as regras base (_base) + as do tipo escolhido num único texto para
 * enviar ao backend. Base sempre; regras do tipo por cima. O nome do tipo entra
 * no topo para o modelo saber que documento está escrevendo.
 */
export async function loadRules(typeId, typeName) {
    const [base, specific] = await Promise.all([
        readRulesJson('_base'),
        readRulesJson(typeId),
    ]);
    const parts = [
        `# Manual ativo do documento: ${typeName}`,
        '## Como interpretar este manual\n' +
            '- Este texto foi gerado a partir dos arquivos `rules.json` do template.\n' +
            '- A base Norven e obrigatoria para estilo, cores, capa, cabecalho, rodape e ferramentas permitidas.\n' +
            '- As regras especificas do tipo definem estrutura, campos obrigatorios e criterios de conteudo.\n' +
            '- Se houver conflito, preserve primeiro regioes protegidas, `styleRef`, paleta, layout e checklist da base.\n' +
            '- Nunca transforme os nomes dos campos do JSON em texto do documento; use-os apenas para decidir como editar.',
        renderRulesJson(base, 'base'),
    ];
    const specificRules = renderRulesJson(specific, 'tipo');
    if (specificRules)
        parts.push(specificRules);
    return parts.filter(Boolean).join('\n\n');
}
/**
 * Insere o `template.docx` do tipo escolhido (capa + cabeçalho/rodapé prontos).
 * `location`:
 *   - 'start'   → insere ANTES do conteúdo existente, sem mais nada: duplicaria
 *                 capa/cabeçalho/rodapé num documento que já tem os seus e abre
 *                 uma página em branco na junção. O fluxo "Formatar" também
 *                 insere o template na frente, mas cuidando disso tudo — ver
 *                 `formatIntoTemplate`.
 *   - 'replace' → SUBSTITUI todo o conteúdo pelo template — garante exatamente
 *                 UMA capa/cabeçalho/rodapé/sumário. É o "Apagar e criar novo" e
 *                 o documento em branco.
 * Falhas nunca interrompem o chat (captura e loga).
 */
export async function applyTemplate(typeId, location = 'start') {
    try {
        const base64 = await fetchBase64(`${TEMPLATES_BASE}/${typeId}/template.docx`);
        const loc = location === 'replace' ? Word.InsertLocation.replace : Word.InsertLocation.start;
        // Tracking SUSPENSO durante a aplicação: com controle de alterações ligado
        // (a propriedade vive NO DOCUMENTO, então pode vir ligada de outra sessão),
        // inserir o template inteiro viraria uma revisão gigante — capa, cabeçalho
        // e corpo todos marcados como inserção. Estabelecer o template é a linha de
        // base do documento, não uma edição a revisar.
        await withTrackChangesSuspended(async () => {
            await Word.run(async (context) => {
                // importStyles/importTheme (WordApi 1.5): traz os ESTILOS e o TEMA do
                // template. Sem isso, o Word mantém os estilos do doc destino (em branco
                // → Heading azul padrão, List Paragraph/Intense Reference 11pt) e descarta
                // os estilos customizados do template — o que deixava os títulos fora do
                // roxo e a capa desformatada (tamanho/cor/posição errados).
                context.document.insertFileFromBase64(base64, loc, {
                    importStyles: true,
                    importTheme: true,
                });
                await context.sync();
                try {
                    await tagCoverShapes(context);
                }
                catch (e) {
                    console.warn('[applyTemplate] não foi possível marcar os campos da capa:', e);
                }
                try {
                    await stampCoverDate(context);
                }
                catch (e) {
                    console.warn('[applyTemplate] não foi possível datar a capa:', e);
                }
                await context.sync();
            });
        });
        return true;
    }
    catch (e) {
        console.warn('[applyTemplate] não foi possível aplicar o template:', e);
        return false;
    }
}
// ============================================================================
// "Formatar": o documento existente ENTRA no template, sem perder nada
// ============================================================================
// Duas versões anteriores perdiam conteúdo. A primeira pedia ao modelo para
// reescrever o documento inteiro (o turno estourava o tempo no meio). A segunda
// extraía o texto e colava de volta no template: as tabelas iam todas para o
// fim, e figuras, quebras de página, listas (tipo e nível), negrito e a data da
// capa se perdiam no caminho (QA BUG-01).
//
// Agora o documento fica onde está e o template entra NA FRENTE dele, como
// seção própria. Medido no Word, nos dois tipos de documento de teste (um feito
// com o template Norven antigo e um de terceiro, com seção em paisagem):
//
//  - `insertFileFromBase64` no nível do DOCUMENTO, em 'Start', faz do template
//    a seção 1 (capa, primeira página diferente, cabeçalho com logo) e deixa as
//    seções do documento com as propriedades delas — a página em paisagem
//    continua em paisagem. Em um Range, o Word descarta a seção do template e a
//    capa cai na seção do documento, com o cabeçalho dele;
//  - a quebra de página que fecha a capa do template, somada à quebra de seção
//    que agora vem depois dela, deixava a página 2 em branco — ela sai;
//  - as seções do documento trazem o cabeçalho/rodapé DELE (no documento feito
//    com o template antigo, o rodapé da 1ª página era o da capa antiga, com a
//    data): recebem o cabeçalho e o rodapé da marca;
//  - a capa antiga (as caixas de texto e a quebra do começo do documento) sai
//    antes, depois de lida: título, cliente e data vão para a capa nova.
//
// A formatação do conteúdo vem depois, por código (`word/formatDocument.ts`).
/** Placeholder de data dos templates — não é a data de um documento real. */
const DATA_DA_CAPA = /\b(\d{1,2}\/\d{1,2}\/\d{4})\b/;
/**
 * O que a capa do documento aberto diz — lido ANTES de mexer nele. Por ordem de
 * confiança: os campos que o mIAu marcou (content controls), as caixas de texto
 * da capa pelo estilo (documento do template antigo, sem marcação), e o título
 * em texto na folha de rosto de um documento de terceiro. A data vem do rodapé
 * da 1ª página. Nada disso inventa: campo que não se acha fica de fora, e a
 * capa nova mostra o placeholder para o usuário ver o que falta.
 */
export async function readOriginalCover() {
    try {
        return await Word.run(async (context) => {
            var _a, _b, _c, _d, _e;
            const capa = {};
            const valor = (t) => {
                const v = (t || '').replace(/\s+/g, ' ').trim();
                return v && !isCoverPlaceholderText(v) ? v : undefined;
            };
            const ccs = context.document.contentControls;
            ccs.load('items/tag,items/text');
            await context.sync();
            for (const cc of ccs.items) {
                if (cc.tag === 'document_title')
                    capa.title = (_a = capa.title) !== null && _a !== void 0 ? _a : valor(cc.text);
                if (cc.tag === 'client_name')
                    capa.client = (_b = capa.client) !== null && _b !== void 0 ? _b : valor(cc.text);
                if (cc.tag === 'document_date')
                    capa.date = (_c = capa.date) !== null && _c !== void 0 ? _c : valor(cc.text);
            }
            if (!capa.title || !capa.client) {
                try {
                    const shapes = context.document.body.shapes;
                    shapes.load('items/type');
                    await context.sync();
                    const caixas = shapes.items
                        .filter((s) => String(s.type) === 'TextBox')
                        .map((s) => {
                        const c = s.body.paragraphs;
                        c.load('items/text,items/style,items/styleBuiltIn');
                        return c;
                    });
                    await context.sync();
                    for (const c of caixas) {
                        for (const p of c.items) {
                            const estilo = `${p.style || ''} ${p.styleBuiltIn || ''}`;
                            if (/\btitle\b|^t[íi]tulo\b/i.test(estilo))
                                capa.title = (_d = capa.title) !== null && _d !== void 0 ? _d : valor(p.text);
                            else if (/client/i.test(estilo))
                                capa.client = (_e = capa.client) !== null && _e !== void 0 ? _e : valor(p.text);
                        }
                    }
                }
                catch (_f) {
                    /* host sem a API de shapes: fica o que os controls deram */
                }
            }
            if (!capa.title) {
                // Folha de rosto em texto: o parágrafo em estilo Título antes da 1ª quebra.
                const ps = context.document.body.paragraphs;
                ps.load('items/text,items/styleBuiltIn');
                await context.sync();
                for (const p of ps.items.slice(0, 15)) {
                    if (String(p.styleBuiltIn) === 'Title') {
                        capa.title = valor(p.text);
                        break;
                    }
                    if ((p.text || '').includes('\f'))
                        break;
                }
            }
            if (!capa.date) {
                const rodape = context.document.sections.getFirst().getFooter(Word.HeaderFooterType.firstPage);
                rodape.load('text');
                await context.sync();
                const data = DATA_DA_CAPA.exec(rodape.text || '');
                if (data && data[1] !== COVER_DATE_PLACEHOLDER)
                    capa.date = data[1];
            }
            return capa;
        });
    }
    catch (e) {
        console.warn('[readOriginalCover] não consegui ler a capa original:', e);
        return {};
    }
}
/**
 * Tira a capa antiga: o run INICIAL de parágrafos sem texto, sem figura inline e
 * fora de tabela — onde as caixas da capa se ancoram, até a quebra que a fecha.
 * Documento que começa direto no texto não perde nada.
 */
async function removeLeadingCover(context) {
    var _a;
    const ps = context.document.body.paragraphs;
    ps.load('items/text,items/tableNestingLevel');
    await context.sync();
    const figuras = ps.items.map((p) => {
        const c = p.inlinePictures;
        c.load('items');
        return c;
    });
    await context.sync();
    let fim = 0;
    while (fim < ps.items.length &&
        !(ps.items[fim].text || '').replace(/\f/g, '').trim() &&
        !figuras[fim].items.length &&
        !((_a = ps.items[fim].tableNestingLevel) !== null && _a !== void 0 ? _a : 0)) {
        fim++;
    }
    // Tudo vazio: não é capa, é documento sem conteúdo — não há o que tirar.
    if (fim === 0 || fim >= ps.items.length)
        return 0;
    ps.items[0].getRange('Whole').expandTo(ps.items[fim - 1].getRange('Whole')).delete();
    await context.sync();
    return fim;
}
/** A quebra de página antes da quebra de seção do template deixava a página 2 em branco. */
async function dropJunctionPageBreak(context) {
    const secoes = context.document.sections;
    secoes.load('items');
    await context.sync();
    if (secoes.items.length < 2)
        return;
    const ps = secoes.items[0].body.paragraphs;
    ps.load('items/text');
    await context.sync();
    if (ps.items.length < 2)
        return;
    const penultimo = ps.items[ps.items.length - 2];
    if ((penultimo.text || '').replace(/\f/g, '').trim())
        return;
    const ox = penultimo.getOoxml();
    await context.sync();
    if (/<w:br[^>]*w:type="page"/i.test(paragraphBodyXml(ox.value || ''))) {
        penultimo.delete();
        await context.sync();
    }
}
/** O `<w:body>` da parte principal de um pacote de `getOoxml()` (ver tools.ts). */
function paragraphBodyXml(ooxml) {
    const parte = /pkg:name="\/word\/document\.xml"[\s\S]*?<\/pkg:part>/i.exec(ooxml);
    const corpo = parte && /<w:body>([\s\S]*)<\/w:body>/i.exec(parte[0]);
    return corpo ? corpo[1] : ooxml;
}
/**
 * Cabeçalho e rodapé da marca (os das páginas de conteúdo do template, o
 * `Primary` da seção 1) em todas as seções do documento — nos três tipos, para
 * uma seção com "primeira página diferente" não mostrar o rodapé da capa antiga.
 */
async function applyTemplateHeaderFooter(context) {
    const secoes = context.document.sections;
    secoes.load('items');
    await context.sync();
    if (secoes.items.length < 2)
        return 0;
    const cabecalho = secoes.items[0].getHeader(Word.HeaderFooterType.primary).getOoxml();
    const rodape = secoes.items[0].getFooter(Word.HeaderFooterType.primary).getOoxml();
    await context.sync();
    const tipos = [
        Word.HeaderFooterType.primary,
        Word.HeaderFooterType.firstPage,
        Word.HeaderFooterType.evenPages,
    ];
    for (const s of secoes.items.slice(1)) {
        for (const tipo of tipos) {
            s.getHeader(tipo).insertOoxml(cabecalho.value, Word.InsertLocation.replace);
            s.getFooter(tipo).insertOoxml(rodape.value, Word.InsertLocation.replace);
        }
    }
    await context.sync();
    return secoes.items.length - 1;
}
/** Título e cliente nos campos marcados da capa nova. */
async function fillCoverFields(context, capa) {
    const preenchidos = [];
    const ccs = context.document.contentControls;
    ccs.load('items/tag');
    await context.sync();
    for (const cc of ccs.items) {
        if (cc.tag === 'document_title' && capa.title) {
            cc.insertText(capa.title, Word.InsertLocation.replace);
            preenchidos.push('title');
        }
        else if (cc.tag === 'client_name' && capa.client) {
            cc.insertText(capa.client, Word.InsertLocation.replace);
            preenchidos.push('client');
        }
    }
    await context.sync();
    return preenchidos;
}
/**
 * Formata o documento ABERTO no tipo `typeId` preservando o conteúdo: tira a
 * capa antiga, põe o template na frente como seção própria, acerta a junção e
 * os cabeçalhos, e leva para a capa nova o que a antiga dizia (`capa`, lida
 * com `readOriginalCover` antes). Lança em erro — quem chama tem a cópia do
 * arquivo e o devolve inteiro.
 */
export async function formatIntoTemplate(typeId, capa) {
    const base64 = await fetchBase64(`${TEMPLATES_BASE}/${typeId}/template.docx`);
    let resultado = { removedCover: 0, contentSections: 0, coverFilled: [] };
    await withTrackChangesSuspended(async () => {
        await Word.run(async (context) => {
            const removedCover = await removeLeadingCover(context);
            context.document.insertFileFromBase64(base64, Word.InsertLocation.start, {
                importStyles: true,
                importTheme: true,
            });
            await context.sync();
            await dropJunctionPageBreak(context);
            const contentSections = await applyTemplateHeaderFooter(context);
            try {
                await tagCoverShapes(context);
            }
            catch (e) {
                console.warn('[formatIntoTemplate] não foi possível marcar os campos da capa:', e);
            }
            const coverFilled = await fillCoverFields(context, capa);
            // A data do documento, não a de hoje; sem data na capa antiga, a de hoje.
            await stampCoverDate(context, capa.date);
            if (capa.date)
                coverFilled.push('date');
            resultado = { removedCover, contentSections, coverFilled };
        });
    });
    return resultado;
}
